/**
 * The Integrations tab rail: what each tab is called, how a tab tells the rail
 * that it needs attention, and the strip itself.
 *
 * All three are here rather than in `IntegrationsPage` because they are one
 * decision — and because a rail that imported its own panels could not be
 * rendered by a verify script at all. Every panel on this screen reaches the
 * API client, which reads `import.meta.env` and evaluates only inside Vite, so
 * the strip takes its panels as a prop and the ARIA contract below is held by a
 * render rather than by the compiler alone. `IntegrationsPage` is what is left:
 * a sentence and two panels.
 *
 * ## The URL is next door, and that is a repair rather than filing
 *
 * `?tab=` — the vocabulary, the narrowing, and {@link integrationTabPath}, which
 * is what a screen elsewhere in the product writes when it means *go and sort
 * out the WhatsApp connection* — lives in `integration-tab-url.ts`, and that
 * file's header carries the argument for a query parameter over a path segment.
 *
 * It was originally all in this file, and the consequence is worth recording
 * because it is the defect this slice's review found: the mechanism was built,
 * defended at length, and **called from nowhere**. Four links across the product
 * meant the WhatsApp connection and every one of them still pointed at bare
 * `/integrations`, which resolves to Email. The callers that had to be fixed
 * include `onboarding-display.ts`, a module whose only import is a type — and a
 * link helper it can reach without pulling in `@wend/ui`, lucide and
 * `react-router-dom` is what stops the next caller writing the string by hand
 * instead.
 *
 * ## Why a tab reports its own status rather than the rail computing one
 *
 * Tabs hide things. That is what they are for, and it is also their one real
 * cost: somebody on the Email tab must be able to see that WhatsApp needs
 * attention without switching to it, or this page has hidden a problem that
 * three stacked cards at least made visible.
 *
 * The rail could work that status out for itself — read `GET /integrations`,
 * derive a verdict per row — and that was rejected. It would put a *second*
 * derivation of "is this connection healthy" on the same screen as the panel's
 * own, and the two would disagree the moment somebody disconnected an account
 * or replaced a credential: the panel would repaint from the response it just
 * received and the rail would still be showing the row it read at page load.
 * A screen lying by adjacency is the defect class this repository keeps
 * catching, and a tab badge sitting a centimetre from the card it contradicts
 * is exactly that shape.
 *
 * So the flow is one-way and the panel is the only source. The Email tab calls
 * {@link useReportIntegrationTabStatus} with the same {@link IntegrationBadge}
 * it renders on its own card header — literally the same object, out of
 * `readSesIntegration`. The WhatsApp panel prefers a callback prop and owns its
 * own vocabulary, so `IntegrationsPage` translates its status through that
 * module's exported tone map at the one call site. Either way the words are the
 * panel's and the rail only draws them.
 *
 * **The status is an `IntegrationBadge` and not a new type** for the same
 * reason: a `{ tone, label }` invented here would be a place for the tab to
 * word a state differently from the card.
 *
 * **A panel outside the rail reports into nothing, silently.** Both entry
 * points no-op without a provider rather than throwing, because these panels
 * are rendered standalone by the verify scripts and a status report is an
 * enhancement to a rail, not a precondition for a card. The provider is
 * `IntegrationTabRail` itself, so there is no second site for it to go missing.
 *
 * **No status is rendered as no mark, never as a healthy one.** A panel that
 * has not loaded yet, or whose load failed, knows nothing about the account;
 * "nothing known" and "working" are different statements and only one of them
 * is ever true. Same rule as every number on these screens.
 *
 * ## Why each tab is drawn as a source card (slice 66)
 *
 * The rail used to be a pill strip — an icon, a word and a glyph. The Blueprint
 * draws each connected account as a source card: an icon tile, a mono kind line
 * ("EMAIL · AMAZON SES") naming the provider, and the channel's name. The rail
 * now wears that skin, and it is still the shared `Tab`: a real `role="tab"`
 * button with roving tabindex and arrow keys, because the card is a *skin*, not
 * a second component that would have to re-implement the tab contract.
 * `SourceCard` itself cannot be the tab — it renders its own `<button>` or
 * `<a>`, and `Tab` owns the button that carries the ARIA state — so the card's
 * classes are composed here from the same pieces SourceCard uses
 * (`CARD_SURFACE.interactive`, `Kicker`, the 30px brand tile). The hole that
 * leaves: if SourceCard's look changes, this rail has to be changed to match.
 *
 * `emphasis` — the tile filled with the brand sweep and a warmer border — is
 * drawn when the panel has reported a `good` status, and only then. It is the
 * panel's verdict, read through the same seam as the glyph; the rail still
 * computes nothing about health. A tab whose panel has not spoken is plain.
 */
import { CARD_SURFACE, Kicker, Tab, TabList, TabPanel, Tabs, cn } from '@wend/ui';
import {
  Circle,
  CircleAlert,
  CircleCheck,
  Mail,
  Megaphone,
  MessageCircle,
  TriangleAlert,
} from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  INTEGRATION_TAB_PARAM,
  INTEGRATION_TAB_VALUES,
  integrationTabFromParam,
  type IntegrationTabId,
} from './integration-tab-url';

import type { BadgeTone } from '@wend/ui';
import type { IntegrationBadge } from '../../lib/integration-display';

/* -------------------------------------------------------------------------- */
/* The tabs themselves                                                         */
/* -------------------------------------------------------------------------- */

/**
 * What each tab is called and what it is drawn with.
 *
 * The word is the channel the tenant recognises — "Email", not "Amazon SES" —
 * because the provider is a detail of the panel and a tenant who has not
 * connected anything yet does not know which provider they are looking for.
 * The panel says which provider under it.
 */
export const INTEGRATION_TAB_DISPLAY: Readonly<
  Record<
    IntegrationTabId,
    {
      readonly label: string;
      /**
       * The provider line above the name — what the account is and whose it is.
       * The tab's name stays the channel; this is the detail under it that the
       * panel used to be the first to say.
       */
      readonly kind: string;
      readonly Icon: typeof Circle;
    }
  >
> = {
  email: { label: 'Email', kind: 'Email · Amazon SES', Icon: Mail },
  whatsapp: { label: 'WhatsApp', kind: 'WhatsApp · Meta Cloud API', Icon: MessageCircle },
  /*
   * "Meta ad account", not "Facebook" and not "Meta": one tab covers every
   * placement of one ad account — Facebook, Instagram and, since slice 46,
   * WhatsApp Status — so naming one would hide the others. The card says which
   * placements underneath.
   *
   * And not "Meta ads", which it was until slice 65 gave that name to Reach's
   * sidebar link, `/reach/ads` — the ads Wend drafted. Two screens with one
   * name are read as one screen; this tab is the *account* those ads run on,
   * which is what the product calls it everywhere else. The id stays
   * `meta-ads`, so `?tab=meta-ads` and every link to it keep working.
   */
  'meta-ads': { label: 'Meta ad account', kind: 'Ads · Meta Marketing API', Icon: Megaphone },
};

/* -------------------------------------------------------------------------- */
/* The mark a tab wears                                                        */
/* -------------------------------------------------------------------------- */

interface StatusMark {
  readonly Icon: typeof Circle;
  readonly colour: string;
}

/**
 * Per tone: the silhouette and its colour, or `null` for a state that gets no
 * mark at all. Keyed by `BadgeTone` so a tone added to the design system fails
 * to compile here rather than rendering a tab with nothing on it by accident.
 *
 * **Every mark is a different shape, not a different colour.** The rail is the
 * one place on this screen where a state is reduced to a single glyph, so it is
 * the one place where colour-blindness or a forced-colours theme would cost the
 * whole signal. The state's own words go with it as screen-reader text, which
 * is what makes the tab announce "Email, sandboxed" rather than "Email".
 *
 * **`neutral` is `null` on purpose.** Neutral is what both channels use for a
 * connection nobody has set up, and a workspace that has not connected WhatsApp
 * has nothing wrong with it. A rail that marked every unconnected channel would
 * be a rail of grey dashes the day Flow's sources arrive, and a warning among
 * five dashes is a warning nobody looks at. The panel says "Not connected" in
 * words, on the tab that is about it.
 */
const STATUS_MARKS: Readonly<Record<BadgeTone, StatusMark | null>> = {
  good: { Icon: CircleCheck, colour: 'text-good' },
  warn: { Icon: TriangleAlert, colour: 'text-warn' },
  bad: { Icon: CircleAlert, colour: 'text-bad' },
  info: { Icon: Circle, colour: 'text-info' },
  neutral: null,
  // Slice 65: `brand` names a part of Wend and is never a status, so no
  // integration verdict carries it; null keeps the rail silent if one ever did.
  brand: null,
  // Slice 66: the identity tones name a kind (a source, a static list) and
  // are never a verdict either, so they get no mark, for the same reason.
  'ident-a': null,
  'ident-b': null,
  'ident-c': null,
  'ident-d': null,
};

/**
 * The status glyph on a tab, or nothing at all when the tab has not spoken.
 *
 * The label is not printed beside the glyph: a rail holding "Email · Nothing to
 * send from" beside "WhatsApp · Token expiring" is a rail that wraps, and the
 * wording belongs on the panel that can also say what to do about it. Here it
 * is announced rather than drawn.
 */
export function IntegrationTabStatusMark({ status }: { status: IntegrationBadge | null }) {
  if (status === null) return null;

  const mark = STATUS_MARKS[status.tone];
  if (mark === null) return null;

  return (
    <>
      <mark.Icon aria-hidden="true" className={cn('size-3.5 shrink-0', mark.colour)} />
      <span className="sr-only">, {status.label}</span>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Reporting                                                                   */
/* -------------------------------------------------------------------------- */

type IntegrationTabStatuses = Partial<Readonly<Record<IntegrationTabId, IntegrationBadge>>>;

interface TabStatusRegistry {
  readonly report: (tab: IntegrationTabId, status: IntegrationBadge | null) => void;
}

const TabStatusContext = createContext<TabStatusRegistry | null>(null);

/**
 * The rail's side of the seam: the statuses to draw, and the registry to hand
 * down.
 *
 * `report` is stable for the life of the page and ignores a report that changes
 * nothing. Both matter: a panel reports from an effect keyed on the tone and
 * the label, so an unstable `report` or a state write that always produced a
 * new object would loop the two of them against each other for as long as the
 * page was open.
 */
function useIntegrationTabStatuses(): {
  statuses: IntegrationTabStatuses;
  registry: TabStatusRegistry;
} {
  const [statuses, setStatuses] = useState<IntegrationTabStatuses>({});

  const report = useCallback((tab: IntegrationTabId, status: IntegrationBadge | null) => {
    setStatuses((current) => {
      const existing = current[tab] ?? null;
      if (existing?.label === status?.label && existing?.tone === status?.tone) return current;

      const next = { ...current };
      if (status === null) delete next[tab];
      else next[tab] = status;
      return next;
    });
  }, []);

  const registry = useMemo<TabStatusRegistry>(() => ({ report }), [report]);

  return { statuses, registry };
}

/**
 * The reporter, for a panel that would rather be handed a callback than call a
 * hook of its own — the WhatsApp tab reports through an `onStatusChange` prop,
 * and the adapter that bridges the two needs this.
 */
export function useIntegrationTabStatusReporter(): (
  tab: IntegrationTabId,
  status: IntegrationBadge | null,
) => void {
  const registry = useContext(TabStatusContext);
  return registry?.report ?? noReport;
}

const noReport = (): void => {};

/**
 * A panel's side of the seam. Call it with the badge the panel is rendering, or
 * `null` while the panel does not yet know.
 *
 * Two effects rather than one, deliberately. The first publishes; the second
 * withdraws on unmount and *only* on unmount, because a single effect with a
 * cleanup would withdraw and republish on every change and blink the mark off
 * and on again each time a check came back.
 *
 * The dependency list carries the tone and the label rather than the badge,
 * because the badge is a fresh object on every render and a badge-keyed effect
 * would fire on every render for ever.
 */
export function useReportIntegrationTabStatus(
  tab: IntegrationTabId,
  status: IntegrationBadge | null,
): void {
  const registry = useContext(TabStatusContext);
  const report = registry?.report;

  const label = status?.label ?? null;
  const tone = status?.tone ?? null;

  useEffect(() => {
    if (report === undefined) return;
    report(tab, label === null || tone === null ? null : { label, tone });
  }, [report, tab, label, tone]);

  useEffect(() => {
    if (report === undefined) return;
    return () => {
      report(tab, null);
    };
  }, [report, tab]);
}

/* -------------------------------------------------------------------------- */
/* The rail                                                                    */
/* -------------------------------------------------------------------------- */

export interface IntegrationTabRailProps {
  /**
   * One panel per tab. A total record over the union, so a tab added to
   * `INTEGRATION_TAB_VALUES` without a panel does not compile — which is the
   * failure that would otherwise ship as a tab that selects and reveals
   * nothing.
   */
  readonly panels: Readonly<Record<IntegrationTabId, ReactNode>>;
}

/**
 * The rail: the strip, the URL that addresses it, and the panels it reveals.
 *
 * It takes its panels rather than importing them, and that is not only a
 * separation of concerns — it is what makes the strip testable. Every panel on
 * this screen reaches the API client, which reaches `lib/config.ts`, which
 * reads `import.meta.env` and evaluates only inside Vite; a rail that imported
 * them could not be rendered by a verify script at all, and the ARIA contract
 * this component exists to get right would be held by nothing but the compiler.
 * With the panels as a prop, `verify-integrations-tabs.tsx` renders the real
 * strip with stand-ins inside it.
 *
 * `keepMounted`: both panels stay mounted and hidden rather than unmounting on
 * the way out, so the tab nobody is looking at is still loading and still able
 * to report that it needs attention — and so a half-typed credential survives
 * somebody glancing at the other channel.
 */
export function IntegrationTabRail({ panels }: IntegrationTabRailProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = integrationTabFromParam(searchParams.get(INTEGRATION_TAB_PARAM));
  const { statuses, registry } = useIntegrationTabStatuses();

  /**
   * Selecting a tab replaces the history entry rather than pushing one.
   *
   * Slice 15's checklist links straight into a tab, and pushing would mean that
   * somebody who arrived from the Overview, glanced at the other tab and
   * pressed Back went to the tab they had just left rather than back to the
   * Overview. The URL still changes, so a reload, a bookmark and a copied link
   * all land where the person is looking, which is the whole requirement.
   *
   * Other parameters are carried through rather than dropped: this screen owns
   * `tab` and nothing else in the query string.
   */
  function select(next: string): void {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        params.set(INTEGRATION_TAB_PARAM, next);
        return params;
      },
      { replace: true },
    );
  }

  return (
    <TabStatusContext.Provider value={registry}>
      <Tabs value={selected} onValueChange={select} keepMounted>
        <TabList
          label="Integrations"
          // Three columns at every width. Below sm the cards compact rather
          // than stack: the tab list is horizontal (Left and Right move, Up and
          // Down are not taken), and a vertical stack where Down does nothing
          // is a keyboard model the eye contradicts (slice 66 review).
          className="grid w-full grid-cols-3 gap-2 overflow-visible rounded-none border-0 bg-transparent p-0 sm:gap-3"
        >
          {INTEGRATION_TAB_VALUES.map((id) => {
            const display = INTEGRATION_TAB_DISPLAY[id];
            const emphasis = statuses[id]?.tone === 'good';
            return (
              <Tab
                key={id}
                value={id}
                className={cn(
                  CARD_SURFACE.interactive,
                  'relative h-full flex-col items-stretch gap-1.5 whitespace-normal p-2.5 text-left text-text sm:p-4',
                  'hover:enabled:bg-transparent',
                  'aria-selected:border-brand-b aria-selected:bg-brand-a/16 aria-selected:shadow-none',
                  'aria-selected:ring-[3px] aria-selected:ring-brand-b/16',
                  emphasis && 'border-brand-b/45',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'grid size-[30px] shrink-0 place-items-center rounded-sm',
                    emphasis ? 'bg-brand-sweep text-white' : 'bg-brand-a/16 text-brand-ink',
                  )}
                >
                  <display.Icon aria-hidden="true" className="size-4" />
                </span>
                <Kicker as="span" tone="brand" className="mt-2 hidden sm:block">
                  {display.kind}
                </Kicker>
                <span className="block text-[0.88rem] font-semibold sm:text-[0.95rem]">{display.label}</span>
                {/* After the label in the markup, drawn top-right: the mark's
                    screen-reader words begin ", Ready to send" and were written
                    to FOLLOW the channel's name. Placed in the top row of the
                    markup (the first cut), the tab was announced status first,
                    with a stray leading comma (slice 66 review). */}
                <span className="absolute right-2.5 top-2.5 inline-flex items-center sm:right-4 sm:top-4">
                  <IntegrationTabStatusMark status={statuses[id] ?? null} />
                </span>
              </Tab>
            );
          })}
        </TabList>

        {/*
          `tabIndex={-1}` on every panel: `TabPanel` is focusable by default so
          that a panel which scrolls on its own is reachable by keyboard, and
          none of these do — they are full of buttons and form fields, which is
          exactly the case the ARIA practices say may be left out of the tab
          order.
        */}
        {INTEGRATION_TAB_VALUES.map((id) => (
          <TabPanel key={id} value={id} tabIndex={-1}>
            {panels[id]}
          </TabPanel>
        ))}
      </Tabs>
    </TabStatusContext.Provider>
  );
}
